import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";

import {
  useMeetingSelector
} from "@kalyzee/kast-web-sdk";
import {
  audioContextIsReadyToUse,
  Input,
  useRefAutoUpdate
} from "@kalyzee/kast-web-sdk/web-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastError } from "../../../../helpers/toast";
import { buildMediasFromSources, MeetingSourceExtended, useMeetingConfigurator, useMeetingContext, useMeetingSources } from "../../../../hooks/meeting";

import { RTCStreamSenderParamsPreset } from "@kalyzee/kast-web-sdk/webrtc";
import { ApiErrorCode } from "@kalyzee/kast-web-sdk/websocket";
import { BoxOverlay, OverlayContainer } from "@kalyzee/kalyzee-ui";
import { getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { getGlobalQueryLabel, NavigateGlobalQuery } from "../../../../helpers/navigation";
import { DEFAULT_MAX_BITRATES } from "../../../../helpers/videos";
import { isMongoDBId } from "../../../../helpers/utils";

import { MeetingContainer } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useAppStoreDispatch } from "../../../../contexts/appStore/context";

import { LoadingView } from "../../../../components/utils/LoadingView";
import { MeetingContainerProvider } from "../../../../contexts/meetingContainer/provider";
import { MeetingUtilsProvider } from "../../../../contexts/meetingUtils/provider";
import { useDeviceControlsContext } from "../../../../contexts/deviceControls/context";
import { useSettings } from "../../../../hooks/settings";
import { useDelayedState } from "../../../../hooks/states";
import authStyles from "../../../public/login/auth.module.css";
import { ConnectedMeetingPage } from "./connected/base";

type Phase = "loading" | "joining" | "connected" | "already_connected" | "denied" | "password_required";

// The server may refuse a join through the meeting join rules (MeetingRulesService).
// MEETING_SESSION_LIMIT_REACHED (1332) is more recent than the bundled SDK enum,
// which stops at MEETING_ALREADY_JOINED_WITH_THIS_USER_ACCOUNT (1331) — hardcode
// its (stable) wire code until the SDK exposes the symbol.
const MEETING_SESSION_LIMIT_REACHED_CODE = 1332;

export const MeetingInitPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);
  const sourcePreferences = useAppStoreSelector(state => state.sourcePreferences);

  const sources = useMeetingSources();

  const dispatch = useAppStoreDispatch();

  const deviceControlsCtx = useDeviceControlsContext();

  const meetingCtx = useMeetingContext();

  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);

  const meetingsAPI = meetingCtx?.api;
  const meetingsAPIRef = useRefAutoUpdate(meetingsAPI);

  const [phase, setPhase] = useState<Phase>("loading");

  const forceToConnectRef = useRef(false);

  const [deniedMessage, setDeniedMessage] = useState<string>("");

  // Password to send with the next join attempt (same pattern as
  // forceToConnectRef). Never persisted: every fresh entry re-prompts.
  const passwordRef = useRef<string>("");
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordInvalid, setPasswordInvalid] = useState(false);

  const container = useMemo(() => {
    if (!meetingsAPI) return undefined;

    const containers = meetingsAPI.getContainers();
    const selectedId = pageContextRef.current?.params?.id;

    if (selectedId) {
      return containers.find((c) => c.getMeetingId() === selectedId);
    }

    return containers[0];
  }, [meetingsAPI]);

  // Avoids flickering
  const [showMeeting] = useDelayedState(container !== undefined, 1000);

  // Bounce back to the lobby — which needs `provider` to resolve
  // provider-scoped ids — or to the list when there is no meeting id at all.
  const exitToLobbyOrList = useCallback((id: string | undefined) => {
    if (id) {
      pageContextRef.current?.goTo(PageMasterMode.LOBBY, { id }, { replace: true, forwardParams: [NavigateGlobalQuery.PROVIDER] });
    } else {
      pageContextRef.current?.goTo(PageMasterMode.LIST);
    }
  }, []);

  const joinContainer = useCallback(async (container: MeetingContainer) => {
    if (container.getStatus() !== "disconnected") return;

    const containerAPI = container.getAPI();

    const medias = buildMediasFromSources(sources as MeetingSourceExtended[], {
      getSourceEnabled: (sourceName) => {
        const pref = sourcePreferences[sourceName];
        return {
          audio: pref?.audioEnabled ?? audioEnabled,
          video: pref?.videoEnabled ?? videoEnabled,
        };
      },
    });

    const result = await containerAPI.join({
      medias,
      disconnectFirstUserIfNeeded: forceToConnectRef.current,
      password: passwordRef.current || undefined,
    });

    const id = pageContextRef.current?.params?.id;

    if (result.error) {
      const code = result.error.code;

      // Errors produced by the meeting join rules (server-side MeetingRulesService).
      // The multi-account case offers a "force" action; the other UNAUTHORIZED_*
      // outcomes are terminal and only bounce the user back.
      if (code === -ApiErrorCode.MEETING_ALREADY_JOINED_WITH_THIS_USER_ACCOUNT) {
        container.destroy();
        setPhase("already_connected");
        return;
      }

      if (code === -ApiErrorCode.MEETING_UNAUTHORIZED_TO_JOIN) {
        container.destroy();
        setDeniedMessage("Vous n'êtes pas autorisé à rejoindre ce meeting.");
        setPhase("denied");
        return;
      }

      if (code === -MEETING_SESSION_LIMIT_REACHED_CODE) {
        // The rule forwards the configured limit in `data.args` for display.
        // (The SDK's ErrorMessage type omits `data`, which the server does send.)
        const limit = (result.error as { data?: { args?: string[] } }).data?.args?.[0];
        container.destroy();
        setDeniedMessage(
          limit
            ? `Ce meeting a atteint sa limite de participants (${limit}).`
            : "Ce meeting a atteint sa limite de participants.",
        );
        setPhase("denied");
        return;
      }

      if (code === -ApiErrorCode.MEETING_PASSWORD_IS_NEEDED || code === -ApiErrorCode.MEETING_INVALID_PASSWORD) {
        container.destroy();
        setPasswordInvalid(code === -ApiErrorCode.MEETING_INVALID_PASSWORD);
        setPasswordInput("");
        setPhase("password_required");
        return;
      }

      console.error(`[ERROR] join: `, result.error);
      toastError(`Une erreur est apparue.`);
      exitToLobbyOrList(id);
      container.destroy();
      return;
    }
    const session = result.response?.session;
    if (!session) {
      console.error(`[ERROR] join - internal error: session is undefined`);
      toastError(`Une erreur est apparue.`);
      exitToLobbyOrList(id);
      container.destroy();
      return;
    }

    deviceControlsCtx.applyEntryPresets();
    setPhase("connected");
  }, [audioEnabled, videoEnabled, sourcePreferences, sources, dispatch, deviceControlsCtx]);

  const joinTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const createAndJoinContainer = useCallback(async (id: string) => {
    const api = meetingsAPIRef.current;

    if (!api) {
      console.error(`[ERROR] join - meetingContext is undefined `);
      toastError(`Une erreur est apparue.`);
      exitToLobbyOrList(id);
      return;
    }

    joinTimeoutRef.current = setTimeout(() => {
      const container = api.getOrCreateContainer(id);
      joinContainer(container);
    }, 0);
  }, [joinContainer]);

  const leave = async () => {
    await deviceControlsCtx.applyExitPresets();
    const promises: Promise<any>[] = [];
    meetingsAPIRef.current?.getContainers()?.forEach((c) => {
      const result = c.getAPI()?.leave();
      if (!("error" in result)) promises.push(result);
    });
    await Promise.allSettled(promises);
  };

  useMeetingConfigurator(container?.getAPI(), {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.name === "screen") {
            const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.screensharing ?? DEFAULT_MAX_BITRATES.student.screensharing;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_SCREENSHARING,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
                },
              },
            };
          }
          const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.webcam ?? DEFAULT_MAX_BITRATES.student.webcam;
          return {
            rtc: {
              streamParams: {
                preset: RTCStreamSenderParamsPreset.VISIO_FACE,
                video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
              },
            },
          };
        },
      },
      reception: {
        options: () => ({
          video: { mode: "subscription", delayToDisableStream: 5000 },
          audio: true,
        }),
      },
    },
  });

  // Phase 1: test audio context
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const id = pageContextRef.current?.params?.id;
      // Provider-scoped URL: id isn't a real meetingId yet — lobby resolves it.
      const hasProvider = !!pageContextRef.current?.searchParams.get(getGlobalQueryLabel(NavigateGlobalQuery.PROVIDER));
      // Short-id URL (anything that isn't a 24-char hex mongoId): lobby resolves it.
      const isShortId = !!id && !isMongoDBId(id);
      if (hasProvider || isShortId) {
        exitToLobbyOrList(id);
        return;
      }

      // Test audio context — if not ready (direct load without gesture), redirect to lobby
      const ready = await audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer());
      if (cancelled) return;

      if (!ready) {
        exitToLobbyOrList(id);
        return;
      }

      setPhase("joining");
    };

    init();

    const onBeforeUnload = () => {
      leave();
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      cancelled = true;
      window.removeEventListener("beforeunload", onBeforeUnload);
      if (joinTimeoutRef.current) clearTimeout(joinTimeoutRef.current);
      leave();
    };
  }, []);

  // Phase: joining → create container and join
  useEffect(() => {
    if (phase !== "joining") return;
    const id = pageContextRef.current?.params?.id ?? "";
    createAndJoinContainer(id);
  }, [phase]);

  if (phase === "loading") {
    return <LoadingView fullscreen message="Chargement du meeting ..." />;
  }

  if (phase === "already_connected") {
    const id = pageContextRef.current?.params?.id;
    return (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        overlay={
          <BoxOverlay
            title="Vous êtes déjà connecté avec ce compte sur un autre appareil"
            level="question"
            buttons={[
              {
                label: "Annuler",
                onClick: () => {
                  exitToLobbyOrList(id);
                },
              },
              {
                label: "Forcer la connexion sur cet appareil",
                onClick: () => {
                  forceToConnectRef.current = true;
                  setPhase("joining");
                },
              },
            ]}
          />
        }
      />
    );
  }

  if (phase === "password_required") {
    const id = pageContextRef.current?.params?.id;
    const submitPassword = () => {
      const password = passwordInput.trim();
      if (!password) return;
      passwordRef.current = password;
      setPhase("joining");
    };
    return (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        overlay={
          <form
            className={authStyles.card}
            style={{ width: "min(90vw, 400px)" }}
            onSubmit={(e) => {
              e.preventDefault();
              submitPassword();
            }}
          >
            <span className={authStyles.title}>Ce meeting est protégé par un mot de passe</span>
            {passwordInvalid && (
              <span style={{ color: "var(--color-element-glass-error)" }}>Mot de passe incorrect.</span>
            )}
            <Input
              className={authStyles.glassInput}
              value={passwordInput}
              setValue={setPasswordInput}
              nativeProps={{ type: "password", placeholder: "Mot de passe", autoFocus: true }}
            />
            <button type="submit" className={authStyles.button} disabled={!passwordInput.trim()}>
              Rejoindre
            </button>
            <button type="button" className={authStyles.link} onClick={() => exitToLobbyOrList(id)}>
              Annuler
            </button>
          </form>
        }
      />
    );
  }

  if (phase === "denied") {
    const id = pageContextRef.current?.params?.id;
    return (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        overlay={
          <BoxOverlay
            title={deniedMessage}
            level="error"
            buttons={[
              {
                label: "Retour",
                onClick: () => {
                  exitToLobbyOrList(id);
                },
              },
            ]}
          />
        }
      />
    );
  }

  if (phase === "joining" || !container || !showMeeting) {
    return <LoadingView fullscreen message="Connexion au meeting ..." />;
  }

  return (
    <MeetingContainerProvider container={container}>
      <MeetingUtilsProvider>
        <ConnectedMeetingPage />
      </MeetingUtilsProvider>
    </MeetingContainerProvider>
  );
};
